<template>
  <div class="details" ref="details" id="form-top">
    <div class="inner-details">
      <div class="form-container">
        <div class="header">
          <h2>{{ title }}</h2>
          <p>{{ subtitle }}</p>
        </div>

        <div class="content">
          <div class="form">
            <!-- Postcode -->
            <div class="wwf-field">
              <label for="postcode">Post code</label>
              <span class="wwf-alert">
                <p v-if="errors.has('postcode')">
                  {{ errors.first("postcode") }}
                </p>
              </span>
              <input
                name="postcode"
                type="text"
                v-model="postcode"
                v-validate="{ required: true, postcodeIsValid: true }"
                data-vv-as="postcode"
                :class="{
                  error: errors.has('postcode'),
                }"
                placeholder="POSTCODE"
                @input="updateElectorates"
                v-on:focus="logTouch('postcode')"
                v-on:blur="logPostcode()"
              />
            </div>

            <!-- Electorates -->
            <div class="wwf-field" v-if="isValidPostcode">
              <label for="electorate">Electorate</label>
              <select required v-model="selectedElectorate">
                <option
                  v-for="electorate in electoratesList"
                  :key="electorate"
                  :value="electorate"
                >
                  {{ electorate }}
                </option>
              </select>
            </div>

            <div class="wwf-group-fields">
              <!-- First Name -->
              <div class="wwf-field">
                <label for="firstName">First name</label>
                <span class="wwf-alert">
                  <p v-if="errors.has('firstName')">
                    {{ errors.first("firstName") }}
                  </p>
                </span>
                <input
                  name="firstName"
                  type="text"
                  v-model="firstName"
                  v-validate="'required'"
                  data-vv-as="first name"
                  :class="{
                    error: errors.has('firstName'),
                    empty: !firstName,
                  }"
                  v-on:focus="logTouch('firstName')"
                  v-on:blur="logFieldComplete('First Name Field Completed')"
                  placeholder="FIRST NAME"
                  autocomplete="given-name"
                />
              </div>

              <!-- Last Name -->
              <div class="wwf-field">
                <label for="lastName">Last name</label>
                <span class="wwf-alert">
                  <p v-if="errors.has('lastName')">
                    {{ errors.first("lastName") }}
                  </p>
                </span>
                <input
                  name="lastName"
                  type="text"
                  v-model="lastName"
                  v-validate="'required'"
                  data-vv-as="last name"
                  :class="{
                    error: errors.has('lastName'),
                    empty: !lastName,
                  }"
                  v-on:focus="logTouch('lastName')"
                  v-on:blur="logFieldComplete('Last Name Field Completed')"
                  placeholder="LAST NAME"
                  autocomplete="family-name"
                />
              </div>
            </div>

            <!-- Email -->
            <div class="wwf-field">
              <label for="email">Email</label>
              <span class="wwf-alert">
                <p v-if="errors.has('email')">
                  {{ errors.first("email") }}
                </p>
              </span>
              <input
                id="email"
                name="email"
                type="email"
                v-model="email"
                v-validate="{ required: true, supportDetailsCheckEmail: true }"
                :class="{
                  error: errors.has('email'),
                  empty: !email,
                }"
                v-on:focus="logTouch('email')"
                v-on:blur="logFieldComplete('Email Address Completed')"
                placeholder="EMAIL ADDRESS"
                autocomplete="email"
              />
            </div>

            <!-- Phone Number -->
            <div class="wwf-field">
              <label for="phone">Phone number</label>
              <span class="wwf-alert">
                <p v-if="errors.has('phone')">
                  {{ errors.first("phone") }}
                </p>
              </span>
              <input
                name="phone"
                type="tel"
                v-model="phone"
                min="8"
                max="12"
                v-validate="{ required: requirePhone, phoneNumberIsValid: true }"
                data-vv-as="phone"
                :class="{
                  error: errors.has('phone'),
                }"
                v-on:focus="logTouch('phone')"
                v-on:blur="logFieldComplete('Phone Number Added')"
                :placeholder="requirePhone ? 'PHONE NUMBER':'PHONE NUMBER'"
                autocomplete="tel"
              />
            </div>

            <!-- Preview email -->
            <button
              type="button"
              class="secondary"
              name="viewMessage"
              @click="toggleMessage"
            >
              <span>View your email to the representative</span>
              <i class="fa fa-angle-up email-arrow" v-if="viewMessage"></i>
              <i class="fa fa-angle-down email-arrow" v-if="!viewMessage"></i>
            </button>

            <!-- opt in -->
            <div class="wwf-field terms">
              <input
                type="checkbox"
                name="terms"
                id="terms"
                v-model="wwfOptIn"
              />
              <label for="terms">By signing you accept WWF-Australia's Privacy Policy</label>
            </div>
          </div>

          <!-- Error msg -->
          <div class="submit-email-error" v-if="isEmailDuplicated">
            <p>You can only submit the petition once.</p>
          </div>

          <!-- Take Action btn -->
          <button
            type="button"
            class="submit-btn"
            name="takeAction"
            v-on:click="takeAction()"
          >
            <span v-if="isSaving">
              <i class="far fa-spinner-third fa-spin"></i>
            </span>
            <span v-else> Take Action </span>
          </button>

          <!-- PRIVACY STATEMENT -->
          <!-- <div class="privacy-policy">
            <p>
              By signing you accept
              <a
                :href="privacyPolicyUrl"
                target="_blank"
                @click="logPrivacy"
              >
                WWF-Australia&rsquo;s Privacy Policy.
              </a>
            </p>
          </div> -->
        </div>
      </div>
      <div class="image-container">
        <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/4796/dist/assets/img/form-image.png" alt="WWF">
      </div>
    </div>
    <div class="message-content" v-if="viewMessage">
      <FormMessageViewer
        :firstName="firstName"
        :lastName="lastName"
        :email="email"
        :postcode="postcode"
        :electorateName="selectedElectorate"
        :isValidPostcode="isValidPostcode"
        :messageId="messageId"
      >
      </FormMessageViewer>

      <div class="back-top" v-if="isValidPostcode">
        <div class="clickable" @click="scrollTop">
          <i class="fa fa-angle-up"></i>
          <p>Back to top</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FormMessageViewer from "./MessageViewer.vue";
import { Validator } from "vee-validate";
import { detect } from "detect-browser";

const browser = detect();

export default {
  name: "FormSupporterDetails",
  components: {
    FormMessageViewer,
  },
  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      postcode: "",
      wwfOptIn: true,

      viewMessage: false,
      isEmailDuplicated: false,
      isValidPostcode: false,
      emailRegex: /.+\@.+\..+/,
      isSaving: false,

      electoratesList: [],
      selectedElectorate: "",

      // Amplitude related variables
      formTouched: false,
      completedBefore: false,
      lastField: "",
    };
  },

  computed: {
    isCompleted() {
      return (
        this.firstName != "" &&
        this.lastName != "" &&
        this.email != "" &&
        this.phone != "" &&
        this.postcode != ""
      );
    },
  },

  props: {
    datalayerName: {
      type: String,
    },

    // MARKETO
    programName: {
      type: String,
      required: true,
    },

    messageId: {
      type: String,
    },

    sendToIve: {
      type: Boolean,
      default: false,
    },
    iveSupplier: {
      type: String,
      required: false,
    },
    iveSource: {
      type: String,
      required: false,
    },
    iveCampaignId: {
      type: String,
      required: false,
    },

    requirePhone:{
      type: Boolean,
      default: true
    },

    thankYouUrl: {
      type: String,
      default: "https://wwf.org.au/thank-you-for-making-a-difference/"
    },

    privacyPolicyUrl: {
      type: String,
      default:"https://wwf.org.au/about-us/privacy-policy/"
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: true,
    },
  },

  created() {
    // Check email
    const supportDetailsCheckEmail = (email) => {
      this.isEmailDuplicated = false;

      if (this.isDonation) {
        return this.checkEmail(email);
      } else {
        // Do not allow multiple submission for the petition
        return new Promise(async (resolve, reject) => {
          try {
            // Check that email is valid
            let result = await this.checkEmail(email)
            if (!result.valid) return resolve(result);

            // Check that email is not a Duplicate
            result = await this.checkEmailDuplicate(email);
            if (!result.valid) return resolve(result);

            this.errors.remove("email");
            resolve({ valid: true });
          } catch(err) {
            console.error(err);
            this.errors.remove("email");
            resolve({ valid: true });
          }
        });
      }
    };

    Validator.extend("supportDetailsCheckEmail", {
      validate: supportDetailsCheckEmail,
      getMessage: (field, params, data) => data.message,
    });

    // Check Phone Number
    let phoneNumberIsValid = (phoneNumber) => {
      if (phoneNumber) {
        const validationRegex = /^(\+?61|0)([0-9]{8,9})/;
        if (!phoneNumber.replace(/ /g, "").match(validationRegex)) {
          return {
            valid: false,
            data: { message: "Invalid phone number" },
          };
        } else {
          return {
            valid: true,
          };
        }
      } else {
        return {
          valid: true,
        };
      }
    };

    Validator.extend("phoneNumberIsValid", {
      validate: phoneNumberIsValid,
      getMessage: (field, params, data) => data.message,
    });

    // Check postcode
    Validator.extend("postcodeIsValid", {
      validate: (postcode) => {
        if (this.isValidPostcode) {
          return { valid: true };
        } else {
          return {
            valid: false,
            data: { message: "Invalid postcode" },
          };
        }
      },
      getMessage: (field, params, data) => data.message,
    });

    window.addEventListener("beforeunload", this.beforeWindowUnload);
  },

  beforeDestroy() {
    window.removeEventListener("beforeunload", this.beforeWindowUnload);
  },

  methods: {
    beforeWindowUnload(e) {
      this.$store.dispatch("logSimpleEvent", {
        eventName: "Species Form exited",
        eventProperties: { "Last Field": this.lastField },
      });
    },
    checkEmail(email) {
      return new Promise((resolve, reject) => {
        if (email.length > 0 && this.emailRegex.test(email)) {
          this.$store
            .dispatch("validateEmail", email)
            .then((response) => {
              if (response) {
                resolve(response);
              }
            })
            .catch((err) => {
              console.error(err);
              resolve({ valid: true });
            });
        } else {
          resolve({
            valid: false,
            data: { message: "Email invalid" },
          });
        }
      });
    },

    checkEmailDuplicate(email) {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch("checkEmail", {
            campaignId: this.datalayerName,
            email,
          })
          .then((result) => {
            if (result) {
              this.isEmailDuplicated = true;
              resolve({
                valid: false,
                field: "email",
                data: { message: "Your email has alreay been submitted" },
              });
            } else {
              resolve({ valid: true });
            }
          });
      });
    },

    async takeAction() {
      if (this.isSaving) return;
      this.isSaving = true;
      try {
        const validate = await this.validate();
        if (validate) {
          await this.savePetition();

          this.firstName = "";
          this.lastName = "";
          this.email = "";
          this.phone = "";
          this.postcode = "";
          this.selectedElectorate = "";
          this.electoratesList = [];
          this.isValidPostcode = false;
          this.wwfOptIn = true;
          this.$emit("submittedPetition");
        }
      } catch (err) {
        this.isSaving = false;
      } finally {
        this.isSaving = false;
      }
    },

    async validate() {
      return new Promise((resolve, reject) => {
        this.$validator
          .validateAll()
          .then((result) => {
            if (result) {
              resolve(result);
            } else {
            document.getElementById("form-header").scrollIntoView({
              behavior: "smooth",
            });
              reject(result);
            }
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    async savePetition() {
      try {
        // TODO: add all needed data to payload
        const payload = {
          email: this.email,
          firstName: this.firstName,
          lastName: this.lastName,
          postcode: this.postcode,
          phone: this.phone,
          ssoId: this.$store.getters.ssoId,
          cookieID: this.$cookie.get("_mkto_trk"),
          keepData: true,
          programName: this.programName,
          electorate: this.selectedElectorate,
          wwfOptIn: this.wwfOptIn,
          sendToIve: this.sendToIve,
          iveSupplier: this.iveSupplier,
          iveSource: this.iveSource,
          iveCampaignId: this.iveCampaignId,
        };

        if (browser) {
          payload.browsername = browser.name;
          payload.browserversion = browser.version;
          payload.browseros = browser.os;
        }

        // Get electorate details
        const mp = await this.$store.dispatch("getMp", this.selectedElectorate);
        if (mp) {
          payload.mpName = mp.name;
          payload.mpEmail = mp.email && mp.email.length > 0 ? mp.email : mp.manualEmail;

          // Find species count
          const [electorateData, heroSpecies, otherSpecies] = await Promise.all([
            this.$store.dispatch("getElectoratesGrade", [
              this.selectedElectorate,
            ]),
            this.$store.dispatch("getHeroSpecies", {
              electorate: this.selectedElectorate,
              ishero: true,
            }),
            this.$store.dispatch("getSpecies", {
              electoraldivisionname: this.selectedElectorate,
              isfeatured: true,
            }),
          ]);

          payload.specieCount =
            electorateData[0].numberofthreatenedspeciesinelectorate;
          payload.specieName = heroSpecies.commonName;

          // Update otheer species store
          this.$store.commit("updateOtherSpecies", otherSpecies);
        }

        console.log("payload", payload);

        // Save petition payload
        await this.$store.dispatch("saveLead", {
          campaignId: this.datalayerName,
          lead: payload,
        });

        // Amplitude logs
        this.$store.dispatch("logSpeciesFormComplete");

        // Open popup
        window.top.location.href = this.thankYouUrl;
        // this.openThankYouPopup();
      } catch (err) {
        console.error("Threatened Species Petition submit error");
        console.error(err);
      }
    },

    openThankYouPopup() {
      const id = "my-backyard-stepup";
      this.$store.dispatch("getContentMessageById", id).then((response) => {
        if (!!response) {
          this.$store.commit("setPopupContent", {
            component: "threatened-species-thank-you-popup",
            componentparams: {
              title: response.description,
              content: response.content,
            },
          });
          this.$store.commit("openCustomContentPopup", true);
        }
      });
    },

    scrollTop() {
      this.$refs.details.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    async updateElectorates(event) {
      const newPostcode = event.target.value;
      try {
        if (newPostcode.length === 4) {
          // Get electorates from postcode
          const electorates = (
            await this.$store.dispatch("getElectorates", newPostcode)
          ).map((e) => e.electoraldivisionname);

          // Handle postcode not found
          if (electorates.length !== 0) {
            this.isValidPostcode = true;
            this.electoratesList = electorates;
            this.selectedElectorate = electorates[0];
          }
        } else {
          this.isValidPostcode = false;
          this.selectedElectorate = "";
          this.electoratesList = [];
        }
      } catch (err) {
        console.error(err);
      }
    },

    toggleMessage() {
      this.viewMessage = !this.viewMessage;
      this.$store.dispatch("logSimpleEvent", {
        eventName: "Expand Letter to Politician",
        eventProperties: { Click: true },
      });
    },

    logTouch(fieldName) {
      // Log first touch
      if (!this.formTouched) {
        this.$store.dispatch("logSpeciesFormBegin");
        this.formTouched = true;
      }

      // Set last touched field
      this.lastField = fieldName;
    },

    logPostcode() {
      console.log("log postcode");
      this.$store.dispatch("logSimpleEvent", {
        eventName: "Postcode",
        eventProperties: { Postcode: this.postcode },
      });

      // Log form complete
      this.logFirstComplete();
    },

    logFieldComplete(eventName) {
      console.log("log", eventName);

      this.$store.dispatch("logSimpleEvent", {
        eventName,
        eventProperties: { valid: true },
      });

      // Log form complete
      this.logFirstComplete();
    },

    logFirstComplete() {
      if (!this.completedBefore && this.isCompleted) {
        console.log("Log first Complete");
        this.$store.dispatch("logSimpleEvent", {
          eventName: "Supporter details completed",
          eventProperties: { "Form Complete": true },
        });
        this.completedBefore = true;
      }
    },

    logPrivacy() {
      console.log("log privacy");

      this.$store.dispatch("logSimpleEvent", {
        eventName: "Privacy Click",
        eventProperties: { Click: true },
      });
    },
  },
};
</script>

<style scoped>
.inner-details {
  display: flex;
  gap: 67px;
}

.form-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.form-container .header h2 {
  font-family: "WWF",sans-serif;
  font-size: 60px;
  line-height: 150%;
  text-transform: uppercase;
}

.form-container .header p {
  margin-bottom: 35px;
  font-family: Open Sans;
  font-size: 18px;
  line-height: 150%;
}

.form-container .content {
  margin-bottom: 1rem;
}

.form-container .content .wwf-group-fields {
  display: flex;
  gap: 24px;
}

.form-container .content .wwf-group-fields .wwf-field {
  flex: 1;
}

.form-container .content .wwf-field input,
.form-container .content .wwf-field select {
  border-radius: 4px;
  border: 1px solid #CCC;
  background: #FFF;
  padding: 10px 40px 10px 16px;
  height: 72px;
}

.form-container .content .wwf-field input::placeholder {
  color: #7B7B7B;
  font-family: "WWF",sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-transform: uppercase;
}

.form-container .content .wwf-field label {
  font-family: "WWF",sans-serif;
  font-size: 24px;
  line-height: normal;
  text-transform: uppercase;
}

.form-container .content .wwf-field.terms {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 1rem;
}

.form-container .content .wwf-field.terms label {
  font-family: Open Sans;
  font-size: 16px;
  text-transform: none;
}

.form-container .content .wwf-field.terms input {
  margin: 0;
}

.form-container .content button.secondary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border-radius: 4px;
  border: 1.5px solid #1C3226;
  padding: 17px 40px;
  margin-bottom: 1rem;
}

.form-container .content button.secondary span {
  font-family: "WWF",sans-serif;
  font-size: 24px;
  line-height: normal;
  text-transform: uppercase;
}

.form-container .content button.submit-btn {
  background-color: #007932;
  width: 100%;
  padding: 12px 0;
}

.form-container .content button.submit-btn span {
  color: var(--white);
  font-family: "WWF",sans-serif;
  font-size: 32px;
  line-height: 150%;
  text-transform: uppercase;
}

.message-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.message-content .message {
  width: 100%;
}

.message-content .back-top .clickable {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.image-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media screen and (max-width: 1350px) {}

@media screen and (max-width: 600px) {
  .inner-details {
    flex-direction: column-reverse;
    gap: 18px;
    padding: 0;
  }

  .form-container .header h2 {
    font-size: 40px;
    text-align: center;
  }

  .form-container .header p {
    text-align: center;
  }

  .form-container .content button.secondary {
    padding: 20px;
  }

  .form-container .content button.secondary span {
    text-align: left;
    line-height: 28px;
    font-size: 23px;
  }
}

@media screen and (max-width: 400px) {}

@media only screen and (max-width: 64em) {}
@media only screen and (max-width: 40em) {}
/* END */
</style>



// WEBPACK FOOTER //
// SupporterDetails.vue?9f198a0c